import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowBottom } from "@coreui/icons";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { createPlasticSeal } from "src/services/plastic-seal.services";

const schema = z.object({
  plasticSeals: z.array(z.any()).min(1, "Please upload your serial number"),
});

const CreatePlasticSealForm = () => {
  const ref = useRef();
  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      plasticSeals: [],
    },
    resolver: zodResolver(schema),
  });

  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    '"Something went wrong. Please try again"'
  );

  const onSubmit = async (data) => {
    await toast
      .promise(createPlasticSeal(data), {
        loading: `Uploading Plastic Seals`,
        success: (response) => {
          const existingSeals = response.data.existingSeals;
          if (
            existingSeals &&
            Array.isArray(existingSeals) &&
            existingSeals.length > 0
          ) {
            const worksheet = XLSX.utils.json_to_sheet(existingSeals);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, worksheet, "ExistingSeals");
            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], {
              type: "application/octet-stream",
            });
            saveAs(blob, "ExistingSeals.xlsx");
          }
          return response.message || "Plastice seals uploaded successfully!!";
        },
        error: (err) => {
          return err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : defaultErrorMessage;
        },
      })
      .catch((err) => {
        // let message =
        //   err.response && err.response.data && err.response.data.message
        //     ? err.response.data.message
        //     : defaultErrorMessage;
        // toast.error(message || "Something went wrong!!");
        // return message;
        console.log(err);
      });
    // Additional logic after all chunks are uploaded (if needed)
    reset();
    ref.current.value = "";
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      const chunkSize = 30000; // Set the chunk size to 30k rows

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const convertedData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          range: 1,
        });
        console.log(convertedData);
        if (convertedData.length <= chunkSize) {
          // If data length is less than or equal to 30k, process it and set into form data
          processAndSetFormData(convertedData);
        } else {
          // If data length is more than 30k, split the data and save the rest as a separate file
          const firstChunk = convertedData.slice(0, chunkSize);
          const remainingData = convertedData.slice(chunkSize);
          processAndSetFormData(firstChunk);
          saveRemainingDataAsFile(remainingData, file.name, [
            "Serial",
            "countryCode",
          ]);
        }
      };

      reader.readAsArrayBuffer(file);
    } else {
      setValue("plasticSeals", []);
    }
  };

  const processAndSetFormData = (data) => {
    let serialArray = [];
    data.forEach((element) => {
      if (Array.isArray(element) && element.length > 0) {
        let obj = {
          value: element[0].toString(),
          countryCode: element[1].toString(),
        };
        serialArray.push(obj);
      }
    });
    setValue("plasticSeals", serialArray);
  };

  const saveRemainingDataAsFile = (
    remainingData,
    originalFileName,
    headers
  ) => {
    // Extract file extension from the original file name
    const fileExtension = originalFileName.split(".").pop();

    // Create a new filename with "remaining-" prefix
    const remainingFileName = `remaining-${originalFileName}`;

    // Add headers to the beginning of the remaining data
    const dataWithHeaders = [headers, ...remainingData];

    const remainingWorkbook = XLSX.utils.book_new();
    const remainingWorksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);
    XLSX.utils.book_append_sheet(
      remainingWorkbook,
      remainingWorksheet,
      "Sheet 1"
    );

    const remainingXlsxBuffer = XLSX.write(remainingWorkbook, {
      bookType: "xlsx",
      type: "array",
    });

    const remainingBlob = new Blob([remainingXlsxBuffer], {
      type: "application/octet-stream",
    });

    // Save the remaining file with the new filename
    saveAs(remainingBlob, remainingFileName);
  };

  const exportToXLSX = () => {
    // Prepare your data to be exported as an array of arrays
    const data = [
      ["Serial", "countryCode"],
      // Add more rows as needed
    ];

    // Create a new workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet 1");

    // Generate the XLSX file buffer
    const xlsxBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Create a Blob from the buffer
    const blob = new Blob([xlsxBuffer], { type: "application/octet-stream" });

    // Save the file using FileSaver.js
    saveAs(blob, "upload-plastic-seal.xlsx");
  };

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              Create Plastic Seal
            </h4>
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CRow>
          <CCol sm={6}>
            <CForm onSubmit={handleSubmit(onSubmit, onError)}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="plasticSeals"
                  className="col-sm-3 col-form-label"
                >
                  File:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileUpload}
                    id="plasticSeals"
                    ref={ref}
                  />
                  <span className="error_text" style={{ fontSize: "14px" }}>
                    We can upload 30k Plastic Seals at once. If your your file
                    contains more than 30k please save the remaining data and
                    upload them again.
                  </span>
                  {errors.plasticSeals?.message && (
                    <p className="error_text">{errors.plasticSeals?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mt-5">
                <CCol sm={12}>
                  <div className="d-flex justify-content-center mt-3">
                    <CButton
                      type="submit"
                      color="primary"
                      className="px-5 py-2"
                    >
                      Submit
                    </CButton>
                  </div>
                </CCol>
                <CCol sm={1}></CCol>
              </CRow>
            </CForm>
          </CCol>
          <CCol sm={2}></CCol>
          <CCol sm={3}>
            <CCardBody>
              Please click on the download button and copy your plastic seal
              data according to the file structure
            </CCardBody>

            <CRow className="mb-3">
              <CButton
                color="secondary"
                download
                style={{ color: "white" }}
                onClick={exportToXLSX}
              >
                {/* <CIcon className="my-4 text-white" icon={cilArrowBottom} height={10} /> */}
                Download Sample
              </CButton>
            </CRow>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>
  );
};

export default CreatePlasticSealForm;
